/**
 * 근로자의 날(노동절) 합산 토글 — Firestore 저장/조회/실시간 구독
 * 설계문서: required/근로자의날.md §6
 *
 * 경로: sharedBooks/{shareBookId}/accountSettings/account_{accountNumber}
 * 필드: labor_day_excluded_employees (string[]) — OFF 인 직원 ID만 저장
 *
 * 의미:
 *  - 빈 배열 / 필드 없음 → 모든 직원 ON (= 합산)
 *  - 배열에 ID 포함 → 해당 직원만 OFF (= 합산 제외, 단 분해/안내는 그대로 노출)
 *
 * 5인 미만 오버라이드(business_scale_override) 와 같은 문서를 공유한다.
 */

import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseService';
import { getAccountSettingsPath } from './businessScaleOverrideService';

const LABOR_DAY_FIELD = 'labor_day_excluded_employees';

const accountSettingsDocRef = (shareBookId: string, accountNumber: number) =>
  doc(db, getAccountSettingsPath(shareBookId), `account_${accountNumber}`);

const parseExcludedIds = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string');
};

/** 단발 조회 (보통은 subscribe 권장) */
export async function getLaborDayExcludedEmployees(
  shareBookId: string,
  accountNumber: number
): Promise<string[]> {
  const snap = await getDoc(accountSettingsDocRef(shareBookId, accountNumber));
  if (!snap.exists()) return [];
  return parseExcludedIds(snap.data()?.[LABOR_DAY_FIELD]);
}

/** 머지 저장. business_scale_override 등 기존 필드는 보존됨. */
export async function setLaborDayExcludedEmployees(
  shareBookId: string,
  accountNumber: number,
  employeeIds: string[]
): Promise<void> {
  await setDoc(
    accountSettingsDocRef(shareBookId, accountNumber),
    {
      [LABOR_DAY_FIELD]: employeeIds,
      updated_at: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * 실시간 구독. 모바일(Flutter) ↔ 웹 동시 사용 시 양방향 반영용.
 * 반환 함수 호출로 구독 해제.
 */
export function subscribeLaborDayExcludedEmployees(
  shareBookId: string,
  accountNumber: number,
  onChange: (excludedIds: string[]) => void,
  onError?: (err: unknown) => void
): () => void {
  const unsub = onSnapshot(
    accountSettingsDocRef(shareBookId, accountNumber),
    (snap) => {
      if (!snap.exists()) {
        onChange([]);
        return;
      }
      onChange(parseExcludedIds(snap.data()?.[LABOR_DAY_FIELD]));
    },
    (err) => {
      onError?.(err);
      onChange([]);
    }
  );
  return unsub;
}
